// Footer.js — sitewide footer: the logo lockup, a one-line summary and the
// X / coffee links, then link columns (Explore, Clubs & tables, Popular
// clubs, About), then the copyright and not-affiliated notice. It links
// every section and the legal pages (About / Contact / Privacy / Terms)
// from every page, for visitors, Search Console and AdSense alike; the
// popular clubs give the club pages sitewide links.

import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CoffeeIcon from "@mui/icons-material/Coffee";
import { BRAND } from "../theme";
import { BrandLockup, XIcon } from "./AppHeader";

const COLUMNS = [
  {
    title: "Explore",
    links: [
      { to: "/", label: "Scoreline grid" },
      { to: "/scorelines", label: "Every scoreline" },
      { to: "/watch", label: "Scorigami Watch" },
      { to: "/records", label: "Records" },
      { to: "/articles", label: "Articles" },
    ],
  },
  {
    title: "Clubs & tables",
    links: [
      { to: "/clubs", label: "All clubs" },
      { to: "/all-time-table", label: "All-time table" },
      { to: "/seasons", label: "Every season" },
      { to: "/head-to-head", label: "Head to head" },
      { to: "/race", label: "Title race chart" },
    ],
  },
  {
    title: "Popular clubs",
    links: [
      { to: "/clubs/arsenal", label: "Arsenal" },
      { to: "/clubs/chelsea", label: "Chelsea" },
      { to: "/clubs/liverpool", label: "Liverpool" },
      { to: "/clubs/manchester-city", label: "Manchester City" },
      { to: "/clubs/manchester-united", label: "Manchester United" },
      { to: "/clubs/tottenham-hotspur", label: "Tottenham Hotspur" },
    ],
  },
  {
    title: "About",
    links: [
      { to: "/about", label: "About" },
      { to: "/faq", label: "FAQ" },
      { to: "/contact", label: "Contact" },
      { to: "/privacy", label: "Privacy policy" },
      { to: "/terms", label: "Terms of use" },
    ],
  },
];

const headingSx = {
  color: "#fff",
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  mb: 1.75,
};

const linkSx = {
  color: BRAND.textSecondary,
  textDecoration: "none",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.4,
  transition: "color 160ms ease",
  "&:hover, &:focus-visible": { color: "#fff" },
};

const pillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1,
  height: 38,
  px: 1.75,
  borderRadius: 999,
  color: "#e6e8ee",
  fontSize: 13.5,
  fontWeight: 700,
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.03)",
  transition: "all 160ms ease",
  "&:hover, &:focus-visible": { color: "#fff", borderColor: BRAND.cyan, background: "rgba(57,208,255,0.08)" },
};

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <Box
      component="footer"
      sx={{
        mt: 8,
        position: "relative",
        zIndex: 5,
        background: "linear-gradient(180deg, rgba(20,20,27,0.85) 0%, rgba(12,12,16,0.95) 100%)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        // The header's brand hairline, mirrored along the top edge.
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          top: -1,
          height: "1px",
          background: "linear-gradient(90deg, transparent, rgba(57,208,255,0.5), rgba(111,93,245,0.5), transparent)",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 }, pb: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "minmax(0, 1.6fr) repeat(4, minmax(0, 1fr))" },
            columnGap: { xs: 3, md: 4 },
            rowGap: { xs: 4, md: 0 },
          }}
        >
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, pr: { md: 3 } }}>
            <Box component={RouterLink} to="/" aria-label="Premier League Scorigami home" sx={{ display: "inline-flex", textDecoration: "none" }}>
              <BrandLockup logoSize={36} topSize={12} bottomSize={27} topGap={0.6} />
            </Box>
            <Typography sx={{ color: BRAND.textSecondary, fontSize: 14, lineHeight: 1.7, mt: 2, maxWidth: 340 }}>
              Every Premier League scoreline since 1992, the results that have never happened, and every club's record.
              Updated after every match.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2.5 }}>
              <Box component="a" href="https://x.com/pl_gami" target="_blank" rel="noopener noreferrer" sx={pillSx}>
                <XIcon size={15} />
                Follow @pl_gami
              </Box>
              <Box component="a" href="https://www.buymeacoffee.com/jar23" target="_blank" rel="noopener noreferrer" sx={pillSx}>
                <CoffeeIcon sx={{ fontSize: 18 }} />
                Buy me a coffee
              </Box>
            </Box>
          </Box>

          {COLUMNS.map((col) => (
            <Box component="nav" key={col.title} aria-label={col.title}>
              <Typography component="p" sx={headingSx}>
                {col.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.25 }}>
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Box component={RouterLink} to={l.to} sx={linkSx}>
                      {l.label}
                    </Box>
                  </li>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            gap: { xs: 1, md: 3 },
            mt: { xs: 5, md: 6 },
            pt: 2.5,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            color: "#80838f",
            fontSize: 12.5,
            lineHeight: 1.6,
          }}
        >
          <Box>© {year} Premier League Scorigami · Built by Mason Maier</Box>
          <Box sx={{ maxWidth: 640, textAlign: { md: "right" } }}>
            An independent fan project, not affiliated with the Premier League or any club. Club names and crests belong to
            their owners.
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
